/**
 * Money decisions for the consumer booking payment step (Stripe.tsx).
 *
 * Pure so it can be unit-tested without rendering. Every gate is numeric:
 * the old `online_deposit_amount > "0"` string comparison treated "0.00"
 * as a deposit, and the old `if (isCancellation)` branch ran a SetupIntent
 * (card save only) even when a deposit was due, then flagged the booking
 * as already paid (HH-99).
 */

import { apiConstants } from "./constants/apiConstants";

type Amount = string | number | null | undefined;

export type BookingPaymentPath =
  | "deposit_payment_intent"
  | "cancellation_setup_intent"
  | "no_payment"
  | "wallet_deposit"
  | "saved_card";

export type PaymentDecisionInput = {
  isDeposit: boolean | undefined;
  depositAmount: Amount;
  isCancellation: boolean | undefined;
  cancelCharge: Amount;
};

const isPositive = (amount: Amount): boolean => {
  const value = Number(amount);
  return Number.isFinite(value) && value > 0;
};

export function depositApplies(
  isDeposit: boolean | undefined,
  depositAmount: Amount
): boolean {
  return Boolean(isDeposit) && isPositive(depositAmount);
}

export function cancellationApplies(
  isCancellation: boolean | undefined,
  cancelCharge: Amount
): boolean {
  return Boolean(isCancellation) && isPositive(cancelCharge);
}

/**
 * Amount keys for the booking payload: deposited_amount only when the
 * deposit applies, cancellation_charges only when the cancellation applies.
 */
export function buildAmountPayload(
  input: PaymentDecisionInput
): Record<string, number> {
  const payload: Record<string, number> = {};
  if (depositApplies(input.isDeposit, input.depositAmount)) {
    payload[apiConstants.deposited_amount] = Number(input.depositAmount);
  }
  if (cancellationApplies(input.isCancellation, input.cancelCharge)) {
    payload[apiConstants.cancellation_charges] = parseFloat(
      Number(input.cancelCharge).toFixed(2)
    );
  }
  return payload;
}

/**
 * New card entered in the form.
 * - deposit_payment_intent: createPaymentMethod -> POST booking/ with no
 *   flags; the backend creates the manual-capture deposit PaymentIntent and
 *   saves the card.
 * - cancellation_setup_intent: setup-intent/ + confirmCardSetup -> POST
 *   booking/ with card_saved_for_cancellation=true.
 * - no_payment: POST booking/ with no flags and no amounts.
 */
export function decideNewCardPath(
  input: PaymentDecisionInput
): "deposit_payment_intent" | "cancellation_setup_intent" | "no_payment" {
  if (depositApplies(input.isDeposit, input.depositAmount)) {
    return "deposit_payment_intent";
  }
  if (cancellationApplies(input.isCancellation, input.cancelCharge)) {
    return "cancellation_setup_intent";
  }
  return "no_payment";
}

/**
 * Apple Pay / Google Pay (PaymentRequest) payment.
 * - wallet_deposit: stripe-terminal/get-payment-intent + confirmCardPayment
 *   -> POST booking/ with is_paid_through_wallet=true and
 *   wallet_payment_intent_id=<paymentIntent.id>.
 * - cancellation_setup_intent: as for a new card.
 */
export function decideWalletPath(
  input: PaymentDecisionInput
): "wallet_deposit" | "cancellation_setup_intent" {
  if (depositApplies(input.isDeposit, input.depositAmount)) {
    return "wallet_deposit";
  }
  return "cancellation_setup_intent";
}

/**
 * Query-string suffix appended to `booking/?stylist_unique_code=...`.
 */
export function buildBookingQuery({
  path,
  walletPaymentIntentId,
}: {
  path: BookingPaymentPath;
  walletPaymentIntentId?: string;
}): string {
  switch (path) {
    case "wallet_deposit":
      return `&is_paid_through_wallet=true&wallet_payment_intent_id=${encodeURIComponent(
        walletPaymentIntentId ?? ""
      )}`;
    case "cancellation_setup_intent":
      return "&card_saved_for_cancellation=true";
    default:
      return "";
  }
}

/**
 * Show the "card will be pre-authorized" note only when the new-card path
 * actually runs a cancellation SetupIntent — not for no_payment (e.g. deposit
 * toggle on with $0 amount).
 */
export function shouldShowCardPreauthNote(
  input: PaymentDecisionInput
): boolean {
  return decideNewCardPath(input) === "cancellation_setup_intent";
}
